import React, { useEffect, useRef, useState } from "react";
import { Container, Navbar, Nav, Card, Button, Form, Row, Col } from "react-bootstrap";
import { Upload, BarChart2, User, FileText } from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from 'axios';
import './App.css'

const UploadPage = ({setLoading, setPercentage, setUploadType, ...props}) => {

  const iconStyle = {
    width: "30px",
    height: "30px",
    color: "#007bff",
  };

  const circleStyle = {
    backgroundColor: "#c4daef",
    borderRadius: "50%",
    width: "60px",
    height: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
  };

  const analyseStyle = {
    backgroundColor: "rgb(255,251,137,0.9)",
    borderRadius: "50%",
    width: "60px",
    height: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto"
  };


  const reviewStyle = {
    backgroundColor: "#75f5b97a",
    borderRadius: "50%",
    width: "60px",
    height: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto"
  }
  
  const reviewIconStyle = {
    color: "#209d63",
    width: "30px",
    height: "30px"
  };

  const analyseIconStyle = {
    color: "rgb(233,153,1)",
    width: "30px",
    height: "30px"
  };

  const URL = 'https://formatcheck.gradschool.mtu.edu/format-check/';
  const API_URL = URL + 'api/';
  const [pdf_file, setPdfFile] = useState(null); 
  const [results, setResults] = useState(null);
  const inputFile = useRef(null);
  const inputFile2 = useRef(null);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setPdfFile(selectedFile);
  };

  const listenToProgress = (fileId) => {
    const eventSource = new EventSource(`${API_URL}progress/${fileId}`);

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      setPercentage(data['percentage']);
      setUploadType('Analysing');

      if (data['percentage'] >= 100) {
        eventSource.close();
        setLoading(false);
        setPercentage(0);
        setUploadType('');
        if (data?.success) {
          setResults(data.results);
        } else {
          alert('failed to check the file.');
        }
        setPdfFile(null);
        inputFile.current.value = "";
        inputFile2.current.value = "";
      }
    };

    eventSource.onerror = (err) => {
      alert('failed to check the file.');
      setLoading(false);
      eventSource.close();
    };
  };

  const analyseFile = (fileType) => {
    if (!pdf_file) {
      return
    }
    const formData = new FormData();
    formData.append('pdf_file', pdf_file);
    formData.append('single_side', fileType ? 1 : 0);
    setLoading(true);
    axios.post(API_URL + 'analyse-pdf', formData, {onUploadProgress: (progressEvent) => {
          const percentComplete = (progressEvent.loaded / progressEvent.total) * 100;
          setPercentage(parseInt(percentComplete));
          setUploadType('Uploading');
        }}).then((response) => {
      listenToProgress(response.data['task_id'])
    }).catch((error) => {setLoading(false); alert('failed to check the file.');});
  }

  useEffect(() => {
    if (results) {
      sessionStorage.removeItem('analysed_data');
      sessionStorage.setItem('analysed_data', JSON.stringify({results: results, url: URL}));
      window.open("/results", "_blank");
    }
  }, [results]);

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "#f0f0f0" }}>
      {/* Header */}
      <Navbar expand="lg" className="border-bottom header-adjustment">
        <Container>
          <Navbar.Brand href="#">
            <FileText className="mtn-5" style={{ ...iconStyle, marginRight: "0.5rem" }} />
            FormatCheck
          </Navbar.Brand>
          <Nav className="ms-auto">
            <User style={iconStyle} />
          </Nav>
        </Container>
      </Navbar>

      {/* Main Content */}
      <Container className="mt-4 flex-grow-1">
        <Row className="text-center mb-4">
          <Col>
            <h2>Check Your PDF Formatting</h2>
            <p>FormatCheck helps ensure your PDFs meet professional standards.</p>
          </Col>
        </Row>

        <Row className="justify-content-center mb-5">
          <Col xs={12} md={6} lg={6} className="mb-3 first-div">
            <Card >
              <Card.Body>
                <Card.Title>Single-sided PDF</Card.Title>
                <Card.Text>Upload a single-sided PDF for formatting analysis.</Card.Text>
                <Form.Group>
                  <Form.Control type="file" ref={inputFile} accept="application/pdf" onChange={handleFileChange}/>
                </Form.Group>
                <Button variant="primary" className="mt-3 w-100" onClick={() => analyseFile(true)}><Upload className="mtn-5" /> Check</Button>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} md={6} lg={6} className="mb-3 second-div">
            <Card>
              <Card.Body>
                <Card.Title>Double-sided PDF</Card.Title>
                <Card.Text>Upload a double-sided PDF for formatting analysis.</Card.Text>
                <Form.Group>
                  <Form.Control type="file" accept="application/pdf" ref={inputFile2} onChange={handleFileChange}/>
                </Form.Group>
                <Button variant="primary" className="mt-3 w-100" onClick={() => analyseFile(false)}><Upload className="mtn-5" /> Check</Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="text-center justify-content-center mt-4 faq">
          <Col xs={12} className="mb-4">
            <h2>How FormatCheck Works</h2>
          </Col>
          <Col xs={12} md={4} lg={4} className="mb-4">
            <div className="mb-3">
              <div style={circleStyle}>
                <Upload style={iconStyle} />
              </div>
            </div>
            <h4>1. Upload</h4>
            <p>Upload your single or double-sided PDF document.</p>
          </Col>
          <Col xs={12} md={4} lg={4} className="mb-4">
            <div className="mb-3">
              <div style={analyseStyle}>
                <BarChart2 style={analyseIconStyle} />
              </div>
            </div>
            <h4>2. Analyze</h4>
            <p>We check your document's formatting.</p>
          </Col>
          <Col xs={12} md={4} lg={4} className="mb-4">
            <div className="mb-3">
              <div style={reviewStyle}>
                <FileText style={reviewIconStyle} />
              </div>
            </div>
            <h4>3. Review</h4>
            <p>Get a report with formatting suggestions.</p>
          </Col>
        </Row>
      </Container>

      <footer className="bg-dark text-light py-4">
        <Container className="text-center">
          <div className="d-flex justify-content-center align-items-center mb-3">
            <FileText style={{ ...iconStyle, marginRight: "0.5rem" }} />
            <span>FormatCheck</span>
          </div>
          <p>Simple PDF formatting checks for your documents.</p>
          <div>
            <a href="#" className="text-light mx-3">Terms of Service</a>
            <a href="#" className="text-light mx-3">Privacy Policy</a>
            <a href="#" className="text-light mx-3">Contact Us</a>
          </div>
          <p className="mt-3" style={{ fontSize: "0.8rem" }}>© 2025 FormatCheck. All rights reserved.</p>
        </Container>
      </footer>
      <Button variant="primary" className="feedback-button" onClick={() => window.open('https://forms.gle/i9KW7v4Qewb34BUe7', "_blank")}>feedback</Button>
    </div>
  );
};

export default UploadPage;
